import { Button } from "@/components/ui/button";
import { TRY } from "@/lib/format";
import { CheckCircle2, ExternalLink, RotateCcw, X, ArrowRight, SkipForward, Layers } from "lucide-react";

const Stat = ({ label, value, tone = "" }) => (
  <div className="rounded-xl border bg-card px-4 py-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-head font-black text-lg tabular-nums mt-0.5 ${tone}`}>{value}</div>
  </div>
);

export default function StepDone({ result, onAgain, onOpen, onClose, nextName, onNext, queuePos }) {
  return (
    <div className="max-w-2xl mx-auto mt-6 space-y-5" data-testid="import-done">
      <div className="rounded-2xl bg-[#0B192C] text-white p-6 relative overflow-hidden">
        <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-400/25 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <span className="h-12 w-12 rounded-2xl bg-emerald-400/20 text-emerald-300 flex items-center justify-center"><CheckCircle2 className="h-6 w-6" /></span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">Aktarım tamamlandı{queuePos ? ` · dosya ${queuePos}` : ""}</div>
            <div className="font-head text-2xl font-black leading-tight" data-testid="import-done-title">{result.contact?.title}</div>
            <div className="text-xs text-slate-300 mt-0.5">{result.contact?.vat_exempt ? "Açık Hesap (KDV'siz)" : "KDV'li cari"}{result.contact?.parent_id ? " · alt cari" : ""}</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat label="Kalem" value={`${result.items} kalem · ${result.entries} kayıt`} />
        <Stat label="Toplam Borç" value={TRY(result.total)} tone="text-primary" />
        <Stat label="Ödemeler" value={`${result.payments} · ${TRY(result.paid)}`} tone="text-emerald-700" />
        <Stat label="Kalan Bakiye" value={TRY(result.balance)} tone={result.balance > 0 ? "text-destructive" : "text-emerald-700"} />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" onClick={onClose} data-testid="import-done-close"><X className="h-4 w-4 mr-1.5" />Kapat</Button>
        {onNext
          ? <Button variant="outline" onClick={onOpen} data-testid="import-open-contact"><ExternalLink className="h-4 w-4 mr-1.5" />Cariyi Aç</Button>
          : <><Button variant="outline" onClick={onAgain} data-testid="import-again"><RotateCcw className="h-4 w-4 mr-1.5" />Sonraki Cariyi Aktar</Button><Button onClick={onOpen} data-testid="import-open-contact"><ExternalLink className="h-4 w-4 mr-1.5" />Cariyi Aç</Button></>}
        {onNext && <Button onClick={onNext} className="min-w-[220px]" data-testid="import-next-file">{nextName ? <>Sonraki dosya: <span className="font-mono ml-1 truncate max-w-[180px]">{nextName}</span><ArrowRight className="h-4 w-4 ml-1.5" /></> : <>Toplu özeti göster<ArrowRight className="h-4 w-4 ml-1.5" /></>}</Button>}
      </div>
    </div>
  );
}

/** Kuyruk bittiğinde tüm dosyaların özeti. */
export function BulkSummary({ queue, onClose, onOpen }) {
  const done = queue.filter((q) => q.status === "done");
  const sum = (k) => done.reduce((s, q) => s + Number(q.result?.[k] || 0), 0);
  return (
    <div className="max-w-3xl mx-auto mt-6 space-y-5" data-testid="import-bulk-summary">
      <div className="rounded-2xl bg-[#0B192C] text-white p-6 relative overflow-hidden">
        <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-primary/30 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <span className="h-12 w-12 rounded-2xl bg-white/10 text-[#ffb27a] flex items-center justify-center"><Layers className="h-6 w-6" /></span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#ffb27a]">Toplu aktarım tamamlandı</div>
            <div className="font-head text-2xl font-black leading-tight" data-testid="import-bulk-title">{done.length} / {queue.length} dosya aktarıldı</div>
            <div className="text-xs text-slate-300 mt-0.5">{queue.filter((q) => q.status === "skipped").length} atlandı · {sum("items")} kalem · {sum("payments")} ödeme</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Toplam Borç" value={TRY(sum("total"))} tone="text-primary" />
        <Stat label="Ödemeler" value={TRY(sum("paid"))} tone="text-emerald-700" />
        <Stat label="Kalan Bakiye" value={TRY(sum("balance"))} tone="text-destructive" />
      </div>
      <div className="rounded-xl border bg-card divide-y" data-testid="import-bulk-list">
        {queue.map((q, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`import-bulk-row-${i}`}>
            {q.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <SkipForward className="h-4 w-4 text-slate-400 shrink-0" />}
            <div className="min-w-0 flex-1"><div className="font-semibold truncate">{q.result?.contact?.title || q.name}</div><div className="text-[11px] text-muted-foreground truncate">{q.name}{q.status === "done" ? ` · ${q.result.items} kalem · ${q.result.payments} ödeme` : " · atlandı"}</div></div>
            {q.status === "done" && <><div className="text-right tabular-nums"><div className="font-bold">{TRY(q.result.total)}</div><div className="text-[11px] text-muted-foreground">bakiye {TRY(q.result.balance)}</div></div>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => onOpen(q.result.contact)} data-testid={`import-bulk-open-${i}`}><ExternalLink className="h-3.5 w-3.5 mr-1" />Aç</Button></>}
          </div>
        ))}
      </div>
      <div className="flex justify-end"><Button onClick={onClose} data-testid="import-bulk-close"><X className="h-4 w-4 mr-1.5" />Kapat</Button></div>
    </div>
  );
}
